import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Navbar from '../home/components/Navbar';
import CustomerServiceCard from './components/CustomerServiceCard';
import MessageForm from './components/MessageForm';
import { getActiveCustomerServices } from '../../api/customerService';

/**
 * 联系客服页：上半部分展示启用中的客服信息（电话/微信/邮箱/工作时间），
 * 下半部分提供留言表单。数据来自公开接口，登录用户与访客均可使用。
 */
export default function CustomerServicePage() {
  const [showForm, setShowForm] = useState(false);

  const { data: services, isLoading, error, refetch } = useQuery({
    queryKey: ['customer-services'],
    queryFn: getActiveCustomerServices,
  });

  const hasServices = (services?.length ?? 0) > 0;

  return (
    <div className="min-h-screen bg-[#f8fafa]">
      <Navbar />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        {/* 页头 */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">联系客服</h1>
            <p className="text-sm text-gray-400 mt-1">有问题需要帮助？选择合适的方式联系我们</p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className={`flex items-center gap-2 px-3 md:px-4 py-2.5 text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
              showForm
                ? 'bg-white text-gray-600 border border-gray-200 hover:border-teal-300 hover:text-teal-600'
                : 'bg-teal-500 hover:bg-teal-600 text-white'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={showForm ? 'ri-arrow-up-s-line' : 'ri-message-2-line'} />
            </div>
            <span>{showForm ? '收起留言' : '留言反馈'}</span>
          </button>
        </div>

        {/* 客服信息 */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-gray-100 rounded w-1/2" />
                    <div className="h-3 bg-gray-100 rounded w-3/4" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mb-3">
              <i className="ri-error-warning-line text-red-400 text-2xl" />
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {error instanceof Error ? error.message : '客服信息加载失败'}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="px-4 py-2 text-sm text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 cursor-pointer transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : !hasServices ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-2xl mb-4">
              <i className="ri-customer-service-2-line text-teal-300 text-3xl" />
            </div>
            <p className="text-base font-medium text-gray-600 mb-1">暂无在线客服</p>
            <p className="text-sm text-gray-400">您可以通过下方留言表单提交问题，我们会尽快回复</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {services!.map((s) => (
              <CustomerServiceCard key={s.id} service={s} />
            ))}
          </div>
        )}

        {/* 留言表单（默认收起，点击「留言反馈」展开；无客服信息时始终展示） */}
        {(showForm || (!isLoading && !hasServices)) && (
          <div className="mt-6 md:mt-8">
            <MessageForm />
          </div>
        )}
      </div>
    </div>
  );
}
